import React from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Collapse,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import {
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon,
} from '@mui/icons-material';
import { buildScopedIncidentsPath } from '../../utils/incidentsNavigation';
import { CameraDiagnostic, DiagnosticReport } from './diagnosticsTypes';

type DiagnosticsReportSectionProps = {
  report: DiagnosticReport;
  hours: number;
  camerasForDisplay: CameraDiagnostic[];
  expandedCameras: Set<string>;
  toggleCameraExpanded: (cameraId: string) => void;
  getStatusIcon: (camera: CameraDiagnostic) => React.ReactNode;
  getStatusColor: (camera: CameraDiagnostic) => 'success' | 'warning' | 'error';
  navigate: (path: string) => void;
  deviceId: string;
  policyPlaneConverged: boolean;
};

export default function DiagnosticsReportSection({
  report,
  hours,
  camerasForDisplay,
  expandedCameras,
  toggleCameraExpanded,
  getStatusIcon,
  getStatusColor,
  navigate,
  deviceId,
  policyPlaneConverged,
}: DiagnosticsReportSectionProps) {
  return (
    <>
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom>
                Total Cameras
              </Typography>
              <Typography variant="h4">{report.summary.total_cameras}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom>
                Total Policies
              </Typography>
              <Typography variant="h4">
                {report.summary.total_policies}
                <Typography component="span" variant="body2" color="text.secondary">
                  {' '}
                  / {report.summary.total_policies_all}
                </Typography>
              </Typography>
              <Typography variant="caption" color="text.secondary">
                enabled / total
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom>
                Total Zones
              </Typography>
              <Typography variant="h4">{report.summary.total_zones}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom>
                Incidents ({hours}h)
              </Typography>
              <Typography variant="h4">{report.summary.total_incidents_24h}</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Paper sx={{ mb: 4 }}>
        <Box sx={{ p: 2 }}>
          <Typography variant="h6" gutterBottom>
            Per-Camera Analysis
          </Typography>
        </Box>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Status</TableCell>
                <TableCell>Camera</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Zones</TableCell>
                <TableCell>Policies</TableCell>
                <TableCell>Incidents ({hours}h)</TableCell>
                <TableCell>Edge Status</TableCell>
                <TableCell>Actions</TableCell>
                <TableCell>Issues</TableCell>
                <TableCell>Details</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {camerasForDisplay.map((camera) => (
                <React.Fragment key={camera.camera_id}>
                  <TableRow>
                    <TableCell>{getStatusIcon(camera)}</TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight="medium">
                        {camera.display_name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {camera.camera_id}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={camera.source_type}
                        size="small"
                        color={camera.source_type === 'hardware' ? 'primary' : 'secondary'}
                      />
                    </TableCell>
                    <TableCell>{camera.zone_count}</TableCell>
                    <TableCell>
                      {camera.policy_count}
                      {camera.policy_count > 0 && (
                        <Typography variant="caption" display="block" color="text.secondary">
                          deployed {camera.deployed_policy_count}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>{camera.incident_count_24h}</TableCell>
                    <TableCell>
                      {camera.edge_alive !== null && (
                        <Chip
                          label={camera.edge_alive ? 'Running' : 'Stopped'}
                          size="small"
                          color={camera.edge_alive ? 'success' : 'error'}
                        />
                      )}
                      {camera.edge_source_reachable !== null && (
                        <Box sx={{ mt: 0.5 }}>
                          <Chip
                            label={camera.edge_source_reachable ? 'Reachable' : 'Unreachable'}
                            size="small"
                            color={camera.edge_source_reachable ? 'success' : 'error'}
                            variant="outlined"
                          />
                        </Box>
                      )}
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => navigate(`/live-view?camera_id=${encodeURIComponent(camera.camera_id)}`)}
                        >
                          Live
                        </Button>
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() =>
                            navigate(
                              `/cameras/${encodeURIComponent(camera.camera_id)}${
                                deviceId ? `?device_id=${encodeURIComponent(deviceId)}` : ''
                              }`
                            )
                          }
                        >
                          Camera
                        </Button>
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() =>
                            navigate(
                              buildScopedIncidentsPath(
                                `/incidents?camera_id=${encodeURIComponent(camera.camera_id)}${
                                  deviceId ? `&device_id=${encodeURIComponent(deviceId)}` : ''
                                }`
                              )
                            )
                          }
                        >
                          Incidents
                        </Button>
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() =>
                            navigate(
                              `/audit?resource_type=camera&resource_id=${encodeURIComponent(camera.camera_id)}`
                            )
                          }
                        >
                          Audit
                        </Button>
                      </Box>
                    </TableCell>
                    <TableCell>
                      {camera.issues.length > 0 ? (
                        <Chip
                          label={`${camera.issues.length} issue(s)`}
                          size="small"
                          color={getStatusColor(camera)}
                        />
                      ) : camera.hints.length > 0 ? (
                        <Chip
                          label={`${camera.hints.length} hint(s)`}
                          size="small"
                          color="warning"
                          variant="outlined"
                        />
                      ) : (
                        <Chip label="OK" size="small" color="success" />
                      )}
                    </TableCell>
                    <TableCell>
                      <IconButton size="small" onClick={() => toggleCameraExpanded(camera.camera_id)}>
                        {expandedCameras.has(camera.camera_id) ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                      </IconButton>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell colSpan={10} sx={{ py: 0, border: 0 }}>
                      <Collapse in={expandedCameras.has(camera.camera_id)}>
                        <Box sx={{ p: 2, bgcolor: 'background.default' }}>
                          {camera.policies.length > 0 && (
                            <Box sx={{ mb: 2 }}>
                              <Typography variant="subtitle2" gutterBottom>
                                Policies ({camera.policies.length})
                              </Typography>
                              <List dense>
                                {camera.policies.map((policy) => (
                                  <ListItem key={policy.policy_id}>
                                    <ListItemText
                                      primary={policy.name}
                                      secondary={`${policy.policy_type} (${policy.policy_id})${
                                        policy.deployment_state
                                          ? ` • ${
                                              policyPlaneConverged &&
                                              String(policy.deployment_state).toUpperCase() !== 'DEPLOYED'
                                                ? `${String(policy.deployment_state).toLowerCase()} (edge converged)`
                                                : String(policy.deployment_state).toLowerCase()
                                            }`
                                          : ''
                                      }`}
                                    />
                                  </ListItem>
                                ))}
                              </List>
                            </Box>
                          )}
                          {camera.issues.length > 0 && (
                            <Box>
                              <Typography variant="subtitle2" gutterBottom color="error">
                                Issues
                              </Typography>
                              <List dense>
                                {camera.issues.map((issue, idx) => (
                                  <ListItem key={idx}>
                                    <ListItemText
                                      primary={issue}
                                      primaryTypographyProps={{ color: 'error' }}
                                    />
                                  </ListItem>
                                ))}
                              </List>
                            </Box>
                          )}
                          {camera.hints.length > 0 && (
                            <Box sx={{ mb: 1 }}>
                              <Typography variant="subtitle2" gutterBottom color="warning.main">
                                Diagnostic hints
                              </Typography>
                              <List dense>
                                {camera.hints.map((hint, idx) => (
                                  <ListItem key={idx}>
                                    <ListItemText
                                      primary={hint}
                                      primaryTypographyProps={{ color: 'text.secondary' }}
                                    />
                                  </ListItem>
                                ))}
                              </List>
                            </Box>
                          )}
                          {camera.issues.length === 0 &&
                            camera.hints.length === 0 &&
                            camera.policies.length === 0 && (
                              <Alert severity="info">
                                Camera is configured but has no policies. Add policies to enable incident generation.
                              </Alert>
                            )}
                        </Box>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                </React.Fragment>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {report.cameras.some((c) => c.issues.length > 0 || c.hints.length > 0) && (
        <Paper>
          <Box sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Recommendations
            </Typography>
            <List>
              {report.cameras
                .filter((c) => c.issues.length > 0 || c.hints.length > 0)
                .map((camera) => (
                  <React.Fragment key={camera.camera_id}>
                    <ListItem>
                      <ListItemText
                        primary={`${camera.display_name} (${camera.camera_id})`}
                        secondary={
                          <Box>
                            {[...camera.issues, ...camera.hints].map((issue, idx) => {
                              let recommendation = '';
                              if (issue.includes('No policies')) {
                                recommendation =
                                  '→ Add policies for this camera (either camera-scoped or zone-based)';
                              } else if (issue.includes('No zones')) {
                                recommendation =
                                  '→ Configure zones for this camera OR add camera-scoped policies';
                              } else if (issue.includes('not applied on edge')) {
                                recommendation =
                                  '→ Deploy and wait for APPLIED/ACTIVATED status before expecting incidents';
                              } else if (issue.includes('not running')) {
                                recommendation = '→ Start the camera pipeline on the edge device';
                              } else if (issue.includes('not reachable')) {
                                recommendation = '→ Check RTSP URL/hardware connection';
                              } else if (issue.includes('No incidents in last')) {
                                recommendation =
                                  '→ This is a hint, not a failure: verify detections in Live View and tune thresholds only if needed';
                              }
                              return (
                                <Typography key={idx} variant="body2" sx={{ mt: 0.5 }}>
                                  {recommendation || `→ ${issue}`}
                                </Typography>
                              );
                            })}
                          </Box>
                        }
                      />
                    </ListItem>
                  </React.Fragment>
                ))}
            </List>
          </Box>
        </Paper>
      )}
    </>
  );
}
